/*
 * Bank payouts "Download CSV": column lists over the view rows the tables render. Pure, unit-tested
 * in csv.test.js. Never a bank detail: no account number (full, masked or last 4), IFSC, bank,
 * branch or the bank's beneficiary name; a teacher is named by name + uuid.
 */
import { EM_DASH } from '../format';
import { csv_file_name } from '../../../util_components/csv_export/to_csv';
import { outcome_chip } from './adapters';

// A view's placeholder dash or "… IST" suffix is display only; the CSV wants the bare value.
export const bare = (v) => {
  if (v === null || v === undefined || v === EM_DASH) return '';
  return typeof v === 'string' ? v.replace(/ IST$/, '') : v;
};

/*
 * A failed export already shows its note by the button; only a lost login goes to the shell
 * (a 403 here is the payments-role gate, not "no module grant").
 */
export const auth_only = (to_error, on_error) => (e, fallback) => {
  if (on_error && to_error(e, fallback).kind === 'auth') on_error(e, fallback);
};

const PAGE = 'Bank payouts';
export const payouts_csv_name = (table, period) => () =>
  csv_file_name({ page: PAGE, table, period });

// Held lines and the bank response preview (lines_table.jsx rows).
export const LINE_COLUMNS = [
  { header: 'Line no', key: 'line_no', kind: 'number' },
  { header: 'Line ID', key: 'line_id', kind: 'number' },
  { header: 'Bank status code', value: (r) => bare(r.status_code) },
  { header: 'Value date', key: 'value_date', kind: 'date' },
  { header: 'UTR', key: 'bank_reference' },
  { header: 'Bank reason', key: 'bank_reason' },
  { header: 'Amount', key: 'amount_raw', kind: 'money' },
  { header: 'Matched', value: (r) => bare(r.match && r.match.label) },
  { header: 'Teacher', key: 'teacher_name' },
  { header: 'Teacher UUID', key: 'teacher_uuid' },
  { header: 'Pay month', key: 'pay_month' },
  { header: 'Payment state', value: (r) => (r.item_state ? r.item_state.label : '') },
  { header: 'Payment amount', key: 'item_amount_raw', kind: 'money' },
  { header: 'Outcome', value: (r) => outcome_chip(r.proposed).label },
  { header: 'Why held', value: (r) => outcome_chip(r.proposed).reason },
  { header: 'Applied', value: (r) => bare(r.applied && r.applied.label) },
  { header: 'Ops action', key: 'ops_action' },
  { header: 'Ops note', key: 'action_note' },
  { header: 'Payout item ID', key: 'payout_item_uuid' },
  { header: 'Bank response ID', key: 'response_uuid' }
];

// Payment not received reports (claims_view rows).
export const CLAIM_COLUMNS = [
  { header: 'Report ID', key: 'claim_uuid' },
  { header: 'Teacher', key: 'teacher_name' },
  { header: 'Teacher UUID', key: 'teacher_uuid' },
  { header: 'Pay month', key: 'pay_month_raw' },
  { header: 'Reported at (IST)', value: (r) => bare(r.created_at), kind: 'datetime' },
  { header: 'Sent on', value: (r) => bare(r.sent_on), kind: 'date' },
  { header: 'UTR', value: (r) => bare(r.bank_reference) },
  { header: 'Amount', key: 'amount_raw', kind: 'money' },
  { header: 'Status', value: (r) => bare(r.status_view && r.status_view.label) },
  { header: 'Teacher wrote', key: 'note' },
  { header: 'Resolution note', key: 'resolution_note' },
  { header: 'Resolved at (IST)', value: (r) => bare(r.resolved_at), kind: 'datetime' },
  { header: 'Payout item ID', key: 'payout_item_uuid' }
];

// Deleted accounts: one CSV row per teacher-month still owed (the table nests months in a cell).
export const deleted_csv_rows = (rows) =>
  (rows || []).reduce((out, r) => {
    const months = r.months && r.months.length ? r.months : [null];
    months.forEach((m) => out.push({ teacher: r, month: m }));
    return out;
  }, []);

export const DELETED_COLUMNS = [
  { header: 'Teacher', value: (x) => x.teacher.teacher_name },
  { header: 'Teacher ID', value: (x) => x.teacher.teacher_id, kind: 'number' },
  { header: 'Teacher UUID', value: (x) => x.teacher.teacher_uuid },
  { header: 'Pay month', value: (x) => (x.month ? x.month.pay_month : '') },
  { header: 'Month status', value: (x) => (x.month ? x.month.lifecycle : '') },
  {
    header: 'Still owed',
    value: (x) => (x.month ? x.month.outstanding_raw : null),
    kind: 'money'
  },
  { header: 'Teacher total due', value: (x) => x.teacher.total_raw, kind: 'money' }
];

// Next bank file (eligibility_view().eligible): who is paid and how much, never where to.
export const ELIGIBLE_COLUMNS = [
  { header: 'Teacher', key: 'teacher_name' },
  { header: 'Teacher UUID', key: 'teacher_uuid' },
  { header: 'Transfer type', value: (r) => bare(r.mode) },
  { header: 'Amount', key: 'amount_raw', kind: 'money' },
  { header: 'Withheld for recovery', key: 'withheld_raw', kind: 'money' },
  { header: 'Paying again after a failed line', key: 'retry', kind: 'bool' },
  { header: 'Checks', value: (r) => (r.flags || []).map((f) => f.label) }
];

export const LEFT_OUT_COLUMNS = [
  { header: 'Teacher', key: 'teacher_name' },
  { header: 'Teacher UUID', key: 'teacher_uuid' },
  { header: 'Reason code', key: 'reason' },
  { header: 'Why not in the file', value: (r) => bare(r.reason_text) },
  { header: 'Still owed', key: 'outstanding_raw', kind: 'money' }
];

// Bank files list (files_view rows): the file's record, not the NEFT file's contents.
export const FILE_COLUMNS = [
  { header: 'Round', value: (f) => `R${f.round_no}` },
  { header: 'Kind', key: 'kind' },
  { header: 'File name', key: 'file_name' },
  { header: 'File ID', key: 'uuid' },
  { header: 'Status', value: (f) => bare(f.status_view && f.status_view.label) },
  { header: 'Payment date', value: (f) => bare(f.payment_date), kind: 'date' },
  { header: 'Lines', key: 'row_count', kind: 'number' },
  { header: 'Line states', key: 'counts_text' },
  { header: 'Total', key: 'total_raw', kind: 'money' },
  { header: 'Made by', value: (f) => bare(f.generated_by) },
  { header: 'Made at (IST)', value: (f) => bare(f.generated_at), kind: 'datetime' },
  { header: 'Downloads', value: (f) => (f.downloads || []).length, kind: 'number' },
  { header: 'Cancelled at (IST)', value: (f) => bare(f.cancelled_at), kind: 'datetime' },
  { header: 'Cancel reason', key: 'cancel_reason' },
  { header: 'Replaces file ID', key: 'replaces_file_uuid' }
];

export const HOLIDAY_COLUMNS = [
  { header: 'Date', key: 'date', kind: 'date' },
  { header: 'Name', value: (h) => bare(h.name) }
];

// Teacher-month pop-up "Payments" (teacher_month_view().items), without the account it went to.
export const ITEM_COLUMNS = [
  { header: 'Round', key: 'round_no' },
  { header: 'Payout item ID', key: 'uuid' },
  { header: 'Our reference', value: (r) => bare(r.reference) },
  { header: 'Close version', key: 'close_version_no', kind: 'number' },
  { header: 'State', value: (r) => bare(r.state_view && r.state_view.label) },
  { header: 'Amount', key: 'amount_raw', kind: 'money' },
  { header: 'Sent on', value: (r) => bare(r.sent_on), kind: 'date' },
  { header: 'UTR', value: (r) => bare(r.bank_reference) },
  { header: 'Why', key: 'why' },
  { header: 'Resolution note', key: 'resolution_note' },
  { header: 'Created at (IST)', value: (r) => bare(r.created_at), kind: 'datetime' }
];
